/** Admins {@link DataTable} for the management page. */
import * as React from 'react';
import { User as UserIcon } from 'lucide-react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { AdminRow } from './AdminRow';
import { AdminCard } from './AdminCard';
import { AdminFiltersContent } from './AdminFilters';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderAdminDetails } from './renderAdminDetails';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { exportAdminsExcel, getAdmins } from '@/services/admins.service';
import { deleteUser } from '@/services/users.service';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  DEFAULT_ADMIN_FILTERS,
  ADMIN_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
  type AdminFilters,
  type FilterGroupId,
} from './admin-configs';
import {
  buildAdminExportQueryParams,
  buildAdminListQueryParams,
  hasAdminActiveFilters,
} from './admin-table-utils';
import type { ViewMode } from '@/types/data-table.types';
import type { Admin } from '@/types/admin.types';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link AdminsDataTable}. */
interface AdminsDataTableProps {
  admins?: Admin[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditAdmin?: (id: string) => void;
  enableFilters?: boolean;
  defaultFilters?: AdminFilters;
  enabledRoles?: string[];
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean; canExportCsv?: boolean };
  showCreateButton?: boolean;
  onCreateAdmin?: () => void;
  toolbarLeft?: React.ReactNode;
}

/** Admins {@link DataTable} for the management page. */
export const AdminsDataTable: React.FC<AdminsDataTableProps> = ({
  admins: externalAdmins,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditAdmin,
  enableFilters = true,
  defaultFilters = DEFAULT_ADMIN_FILTERS,
  enabledRoles = ['admin', 'coordinator', 'manager'],
  layoutWidth: layoutWidthProp = 'full',
  isSection = false,
  pageActions,
  showCreateButton = false,
  onCreateAdmin,
  toolbarLeft,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;

  const dt = useDataTable<AdminFilters, FilterGroupId>({
    tableName: 'admins',
    defaultFilters,
    allFilterGroupIds: ADMIN_FILTER_GROUP_IDS,
    layoutWidth: layoutWidthProp,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  React.useEffect(() => {
    dt.setCurrentPage(1);
  }, [enabledRoles]);

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['users', 'admin', dt.currentPage, dt.itemsPerPage, dt.filters, enabledRoles],
    queryFn: async () => {
      return await getAdmins(
        buildAdminListQueryParams(dt.filters, {
          page: dt.currentPage,
          pageSize: dt.itemsPerPage,
          roleCodes: enabledRoles,
        }),
      );
    },
    enabled: !externalAdmins,
  });

  const data = React.useMemo(
    () => (externalAdmins ?? (apiResponse?.data || [])) as Admin[],
    [externalAdmins, apiResponse?.data],
  );
  const totalItems = apiResponse?.total || data.length;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = React.useMemo(
    () => (externalAdmins ? data.length === 0 : !isLoading && totalItems === 0),
    [externalAdmins, data.length, isLoading, totalItems],
  );

  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportAdminsExcel(buildAdminExportQueryParams(dt.filters, enabledRoles));
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [dt.filters, enabledRoles, isEmptyForExport]);

  const {
    data: expandedAdminData,
    isLoading: isLoadingExpandedAdmin,
    isError: isExpandedAdminError,
    error: expandedAdminError,
  } = useQuery({
    queryKey: ['admins', expandedRowId],
    queryFn: async () => {
      if (!expandedRowId) return null;
      const { getAdmin } = await import('@/services/admins.service');
      return await getAdmin(expandedRowId);
    },
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedAdminError,
    error: expandedAdminError,
    catalogKey: 'adminNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const hasActiveFilters = React.useMemo(() => hasAdminActiveFilters(dt.filters), [dt.filters]);

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      dt.setDeleteId(null);
      toast.success('Administrateur supprimé avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const adminToDelete = React.useMemo(
    () => data.find((admin) => admin.id === dt.deleteId),
    [data, dt.deleteId],
  );

  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setEstablishment = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, establishmentId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setProgramIds = React.useCallback(
    (ids: string[] | undefined) => {
      dt.setFilters((prev) => ({ ...prev, programIds: ids }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setDepartmentCodes = React.useCallback(
    (codes: string[] | undefined) => {
      dt.setFilters((prev) => ({ ...prev, departmentCodes: codes }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSearchQuery = React.useCallback(
    (query: string) => {
      dt.setFilters((prev) => ({ ...prev, searchQuery: query }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setDateRange = React.useCallback(
    (range: AdminFilters['dateRange']) => {
      dt.setFilters((prev) => ({ ...prev, dateRange: range }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({ ...prev, customDateFrom: from, customDateTo: to }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <AdminFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleStatus={toggleStatusFilter}
        onSetEstablishment={setEstablishment}
        onSetProgramIds={setProgramIds}
        onSetDepartmentCodes={setDepartmentCodes}
        onSetSearchQuery={setSearchQuery}
        onSetDateRange={setDateRange}
        onSetCustomDateRange={setCustomDateRange}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      toggleStatusFilter,
      setEstablishment,
      setProgramIds,
      setDepartmentCodes,
      setSearchQuery,
      setDateRange,
      setCustomDateRange,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters' as const,
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const expandedAdmin = (expandedAdminData ?? null) as Admin | null;

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="administrateurs"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={6}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                const next = !dt.showFilters;
                if (!next) dt.setShowConfigure(false);
                dt.setShowFilters(next);
              }}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter la liste des administrateurs en Excel'
              }
            />
          )}
          {showCreateButton && onCreateAdmin && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer un collaborateur"
              onClick={() => {
                onCloseDetails?.();
                onCreateAdmin();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={dt.clearFilters}
            resultCount={totalItems}
            resultLabel="administrateur(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ADMIN_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map(
                  (id) => {
                    const isOn = dt.filterVisibility[id] ?? true;
                    return (
                      <Button
                        key={id}
                        variant="custom"
                        size="sm"
                        onClick={() => dt.handleToggleFilterVisibility(id)}
                        disabled={dt.isSavingFilterVisibility}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                          dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                          isOn
                            ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                            : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                        )}
                      >
                        {FILTER_GROUP_LABELS[id]}
                      </Button>
                    );
                  },
                )}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Nom</th>
          <th>Contact</th>
          <th>Rôles</th>
          <th>Statut</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(admin) => (
        <AdminRow
          admin={admin}
          interfaceElements={dt.interfaceElements}
          onView={onToggleRow ? () => onToggleRow(admin.id) : undefined}
          onEdit={onEditAdmin}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(admin) => (
        <DetailsViewContent>{renderAdminDetails(admin)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(admin) => (
        <AdminCard
          admin={admin}
          interfaceElements={dt.interfaceElements}
          onView={onToggleRow ? () => onToggleRow(admin.id) : undefined}
          onEdit={onEditAdmin}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={UserIcon}
      emptyText="Aucun administrateur trouvé"
      title="Administrateurs"
      sidebarMenuId="menu-admins"
      isSection={isSection}
      toolbarLeft={toolbarLeft}
      expandedItem={expandedAdmin}
      isExpandedDetailLoading={isLoadingExpandedAdmin}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(admin) => (
        <DetailsViewContent>{renderAdminDetails(admin)}</DetailsViewContent>
      )}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
          }}
          itemName={adminToDelete ? `${adminToDelete.firstName} ${adminToDelete.lastName}` : ''}
          isLoading={deleteMutation.isPending}
        />
      }
    />
  );
};
